import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, Title, Tooltip, Legend, Filler
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const VIEW_MODES = [
  { id: 'total',   label: 'Total' },
  { id: 'channel', label: 'Online vs POS' },
  { id: 'orders',  label: 'Orders' },
];

export default function SalesChart({ data, full }) {
  const [view, setView] = useState('channel');

  if (!data || !data.length) return <div className="inv-empty">No sales data available</div>;

  const labels = data.map(d => d.date.slice(5));

  const datasets = {
    total: [
      {
        label: 'Revenue (AED)',
        data: data.map(d => d.revenue),
        borderColor: '#00ff88',
        backgroundColor: 'rgba(0,255,136,0.08)',
        fill: true, tension: 0.4, pointRadius: 0, pointHoverRadius: 6,
        borderWidth: 2, yAxisID: 'y',
      },
      {
        label: 'Orders',
        data: data.map(d => d.orders),
        borderColor: '#00d4ff',
        backgroundColor: 'rgba(0,212,255,0.05)',
        fill: false, tension: 0.4, pointRadius: 0, pointHoverRadius: 6,
        borderWidth: 2, yAxisID: 'y1',
      },
    ],
    channel: [
      {
        label: '🌐 Online Revenue',
        data: data.map(d => d.online_revenue),
        borderColor: '#00ff88',
        backgroundColor: 'rgba(0,255,136,0.10)',
        fill: true, tension: 0.4, pointRadius: 0, pointHoverRadius: 6,
        borderWidth: 2, yAxisID: 'y',
      },
      {
        label: '🏪 POS Revenue',
        data: data.map(d => d.pos_revenue),
        borderColor: '#ff9f40',
        backgroundColor: 'rgba(255,159,64,0.10)',
        fill: true, tension: 0.4, pointRadius: 0, pointHoverRadius: 6,
        borderWidth: 2, yAxisID: 'y',
      },
    ],
    orders: [
      {
        label: '🌐 Online Orders',
        data: data.map(d => d.online_orders),
        borderColor: '#00ff88',
        backgroundColor: 'rgba(0,255,136,0.08)',
        fill: true, tension: 0.4, pointRadius: 0, pointHoverRadius: 6,
        borderWidth: 2, yAxisID: 'y',
      },
      {
        label: '🏪 POS Orders',
        data: data.map(d => d.pos_orders),
        borderColor: '#ff9f40',
        backgroundColor: 'rgba(255,159,64,0.08)',
        fill: true, tension: 0.4, pointRadius: 0, pointHoverRadius: 6,
        borderWidth: 2, yAxisID: 'y',
      },
    ],
  };

  const isRevenue = view !== 'orders';
  const lineData = { labels, datasets: datasets[view] };

  const options = {
    responsive: true, maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        labels: { color: '#a0a0b0', font: { family: 'Inter', size: 12 }, usePointStyle: true }
      },
      tooltip: {
        backgroundColor: 'rgba(22,22,42,0.95)', titleColor: '#fff', bodyColor: '#a0a0b0',
        borderColor: 'rgba(0,255,136,0.3)', borderWidth: 1, padding: 12,
        callbacks: {
          label: (ctx) => isRevenue
            ? `${ctx.dataset.label}: AED ${ctx.parsed.y.toLocaleString()}`
            : `${ctx.dataset.label}: ${ctx.parsed.y}`
        }
      }
    },
    scales: {
      x: {
        grid: { color: 'rgba(255,255,255,0.03)' },
        ticks: { color: '#606080', font: { size: 10 }, maxTicksLimit: 15 }
      },
      y: {
        position: 'left',
        grid: { color: 'rgba(255,255,255,0.03)' },
        ticks: {
          color: '#a0a0b0', font: { size: 10 },
          callback: isRevenue ? v => `AED ${v.toLocaleString()}` : v => v
        }
      },
      ...(view === 'total' ? {
        y1: {
          position: 'right',
          grid: { drawOnChartArea: false },
          ticks: { color: '#00d4ff', font: { size: 10 } }
        }
      } : {})
    }
  };

  // summary totals for the current view
  const onlineTotal = data.reduce((s, d) => s + (view === 'orders' ? d.online_orders : d.online_revenue), 0);
  const posTotal    = data.reduce((s, d) => s + (view === 'orders' ? d.pos_orders    : d.pos_revenue), 0);
  const grandTotal  = onlineTotal + posTotal;

  return (
    <div className="inv-chart-container">
      <div className="inv-chart-header">
        <h2 className="inv-chart-title">
          <span>📈</span> Sales Performance
          <span className="inv-badge">{data.length} days</span>
        </h2>
        <div className="inv-chart-view-tabs">
          {VIEW_MODES.map(m => (
            <button
              key={m.id}
              className={`inv-chart-tab ${view === m.id ? 'active' : ''}`}
              onClick={() => setView(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {view === 'channel' && grandTotal > 0 && (
        <div className="inv-chart-channel-summary">
          <div className="inv-ccs-item">
            <span className="inv-ccs-dot online"></span>
            <span className="inv-ccs-label">Online</span>
            <span className="inv-ccs-val">AED {onlineTotal.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
            <span className="inv-ccs-pct">{(onlineTotal / grandTotal * 100).toFixed(1)}%</span>
          </div>
          <div className="inv-ccs-bar">
            <div style={{ width: `${onlineTotal / grandTotal * 100}%`, background: '#00ff88', height: '100%', borderRadius: '4px 0 0 4px' }}></div>
            <div style={{ width: `${posTotal / grandTotal * 100}%`, background: '#ff9f40', height: '100%', borderRadius: '0 4px 4px 0' }}></div>
          </div>
          <div className="inv-ccs-item">
            <span className="inv-ccs-dot pos"></span>
            <span className="inv-ccs-label">POS</span>
            <span className="inv-ccs-val">AED {posTotal.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
            <span className="inv-ccs-pct">{(posTotal / grandTotal * 100).toFixed(1)}%</span>
          </div>
        </div>
      )}

      {view === 'orders' && grandTotal > 0 && (
        <div className="inv-chart-channel-summary">
          <div className="inv-ccs-item">
            <span className="inv-ccs-dot online"></span>
            <span className="inv-ccs-label">Online</span>
            <span className="inv-ccs-val">{onlineTotal} orders</span>
            <span className="inv-ccs-pct">{(onlineTotal / grandTotal * 100).toFixed(1)}%</span>
          </div>
          <div className="inv-ccs-bar">
            <div style={{ width: `${onlineTotal / grandTotal * 100}%`, background: '#00ff88', height: '100%', borderRadius: '4px 0 0 4px' }}></div>
            <div style={{ width: `${posTotal / grandTotal * 100}%`, background: '#ff9f40', height: '100%', borderRadius: '0 4px 4px 0' }}></div>
          </div>
          <div className="inv-ccs-item">
            <span className="inv-ccs-dot pos"></span>
            <span className="inv-ccs-label">POS</span>
            <span className="inv-ccs-val">{posTotal} orders</span>
            <span className="inv-ccs-pct">{(posTotal / grandTotal * 100).toFixed(1)}%</span>
          </div>
        </div>
      )}

      <div style={{ height: full ? '500px' : '350px', position: 'relative' }}>
        <Line data={lineData} options={options} />
      </div>
    </div>
  );
}
